import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import {
  RuleResponse,
  RuleRequest,
  BusinessNameResponse,
  BusinessNameRequest,
  BusinessAddressResponse,
  BusinessAddressRequest,
  TargetAudienceResponse,
  TargetAudienceRequest,
  GeographicFocusResponse,
  GeographicFocusRequest,
  RobotsRuleResponse,
  TechnicalRuleResponse,
} from './apiTypes';
import { emptyApiSlice } from './emptyApiSlice';

export const rulesApiSlice = emptyApiSlice.injectEndpoints({
  endpoints: builder => ({
    getConversationalWritingRule: builder.query<RuleResponse, RuleRequest>({
      query: requestData => ({
        url:
          '/v1/visibility/rules/conversational-writing' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['VisibilityRules'],
      transformResponse: (raw: RuleResponse, meta: FetchBaseQueryMeta) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    getStatisticalEvidenceRule: builder.query<RuleResponse, RuleRequest>({
      query: requestData => ({
        url:
          '/v1/visibility/rules/statistical-evidence' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['VisibilityRules'],
      transformResponse: (raw: RuleResponse, meta: FetchBaseQueryMeta) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    getContentFreshnessRule: builder.query<RuleResponse, RuleRequest>({
      query: requestData => ({
        url:
          '/v1/visibility/rules/content-freshness' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['VisibilityRules'],
      transformResponse: (raw: RuleResponse, meta: FetchBaseQueryMeta) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    businessName: builder.query<BusinessNameResponse, BusinessNameRequest>({
      query: requestData => ({
        url:
          '/v1/informationcompleteness/rules/businessname' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['BusinessName'],
      transformResponse: (
        raw: BusinessNameResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    businessAddress: builder.query<
      BusinessAddressResponse,
      BusinessAddressRequest
    >({
      query: requestData => ({
        url:
          '/v1/informationcompleteness/rules/businessaddress' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['BusinessAddress'],
      transformResponse: (
        raw: BusinessAddressResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    targetAudience: builder.query<
      TargetAudienceResponse,
      TargetAudienceRequest
    >({
      query: requestData => ({
        url:
          '/v1/informationcompleteness/rules/targetaudience' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['TargetAudience'],
      transformResponse: (
        raw: TargetAudienceResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    geographicFocus: builder.query<
      GeographicFocusResponse,
      GeographicFocusRequest
    >({
      query: requestData => ({
        url:
          '/v1/informationcompleteness/rules/geographicfocus' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['GeographicFocus'],
      transformResponse: (
        raw: GeographicFocusResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    getRobotsTxtRule: builder.query<RobotsRuleResponse, RuleRequest>({
      query: requestData => ({
        url:
          '/reportdata/accessibility/rules/robotstxt' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['VisibilityRules'],
      transformResponse: (
        raw: RobotsRuleResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    getLLmsRule: builder.query<TechnicalRuleResponse, RuleRequest>({
      query: requestData => ({
        url:
          '/reportdata/accessibility/rules/llmstxt' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['VisibilityRules'],
      transformResponse: (
        raw: TechnicalRuleResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
    getSchemaRule: builder.query<TechnicalRuleResponse, RuleRequest>({
      query: requestData => ({
        url:
          '/reportdata/accessibility/rules/schemaorg' +
          (requestData.reportId
            ? `?report-id=${encodeURIComponent(requestData.reportId)}`
            : ''),
        method: 'POST',
        body: requestData,
      }),
      providesTags: ['VisibilityRules'],
      transformResponse: (
        raw: TechnicalRuleResponse,
        meta: FetchBaseQueryMeta
      ) => ({
        ...raw,
        statusCode: meta?.response?.status,
      }),
    }),
  }),
});

export const {
  useGetConversationalWritingRuleQuery,
  useGetStatisticalEvidenceRuleQuery,
  useGetContentFreshnessRuleQuery,
  useBusinessNameQuery,
  useBusinessAddressQuery,
  useTargetAudienceQuery,
  useGeographicFocusQuery,
  useGetLLmsRuleQuery,
  useGetSchemaRuleQuery,
  useGetRobotsTxtRuleQuery,
  useLazyGetConversationalWritingRuleQuery,
  useLazyGetStatisticalEvidenceRuleQuery,
  useLazyGetContentFreshnessRuleQuery,
  useLazyBusinessNameQuery,
  useLazyBusinessAddressQuery,
  useLazyTargetAudienceQuery,
  useLazyGeographicFocusQuery,
  useLazyGetLLmsRuleQuery,
  useLazyGetSchemaRuleQuery,
  useLazyGetRobotsTxtRuleQuery,
} = rulesApiSlice;
